import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  GoogleIdentity,
  GoogleRegister,
  LoginData,
  Register,
  UserCredentials,
} from '@interfaces/User';
import { environment } from 'environments/environment';
import { BehaviorSubject, finalize, firstValueFrom, Observable } from 'rxjs';

import {
  ApiResponse,
  RegistrationResponse,
} from '../../interfaces/httpResponses';
import { skipLoaderHeaders } from '../../interceptors/loader.interceptor';
import { ApiErrorAdapter } from '../shared/handlers/api-error-adapter.service';
import { NotificationService } from '../shared/handlers/notification.service';
import { AccountService } from './account.service';
import { ArcaElectronicBillingService } from './arca-electronic-billing.service';
import { GoogleIdentityService } from './google-identity.service';
import { PLATFORM_NOTICES_SESSION_KEY } from './platform-announcements.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly AUTH_URL: string = `${environment.apiUrl}auth`;
  private readonly ACCESS_TOKEN: string = 'access_token';
  private readonly GOOGLE_REGISTRATION_PENDING_KEY =
    'google_registration_pending';

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private router: Router,
    private httpClient: HttpClient,
    private notificationService: NotificationService,
    private accountService: AccountService,
    private googleIdentityService: GoogleIdentityService,
    private apiErrors: ApiErrorAdapter,
    private arcaElectronicBillingService: ArcaElectronicBillingService,
  ) {}

  get isLoading(): boolean {
    return this.loadingSubject.value;
  }

  get isLoggedUser(): boolean {
    return !!localStorage.getItem(this.ACCESS_TOKEN);
  }

  get isGoogleAuthEnabled(): boolean {
    return this.googleIdentityService.isEnabled;
  }

  public onLogin(userCredentials: UserCredentials): void {
    this.loadingSubject.next(true);

    this.httpClient
      .post<ApiResponse<LoginData>>(`${this.AUTH_URL}/login`, userCredentials, {
        headers: skipLoaderHeaders(),
      })
      .pipe(finalize(() => this.loadingSubject.next(false)))
      .subscribe({
        next: (data) => this.persistSuccessfulLogin(data),
        error: (error: unknown) => {
          this.notificationService.showToastError(
            error,
            'Error al iniciar sesión. Verifica tus credenciales o intenta nuevamente.',
          );
        },
      });
  }

  public onRegister(newAccount: Register, logo: File): void {
    this.loadingSubject.next(true);

    const company = {
      ...newAccount.company,
      logo: undefined,
    };
    const formData = new FormData();

    formData.append('company', JSON.stringify(company));
    formData.append('user', JSON.stringify(newAccount.user));
    formData.append('logo', logo, logo.name);

    this.httpClient
      .post<ApiResponse<RegistrationResponse>>(
        `${this.AUTH_URL}/register`,
        formData,
        { headers: skipLoaderHeaders() },
      )
      .pipe(finalize(() => this.loadingSubject.next(false)))
      .subscribe({
        next: (data) => this.finishRegistration(data),
        error: (error: unknown) => {
          this.notificationService.showToastError(
            error,
            'No se pudo completar el registro.',
          );
        },
      });
  }

  public async startGoogleLogin(): Promise<void> {
    await this.runGoogleLogin(() => this.googleIdentityService.startSignIn());
  }

  public async completeGoogleLogin(): Promise<void> {
    await this.runGoogleLogin(() =>
      this.googleIdentityService.completeRedirectSignIn(),
    );
  }

  public async startGoogleRegistration(): Promise<GoogleIdentity | null> {
    return this.runIdentityAction(() =>
      this.googleIdentityService.startSignIn(),
    );
  }

  public async getGoogleRegistrationIdentity(): Promise<GoogleIdentity | null> {
    if (!this.isGoogleAuthEnabled) return null;

    return this.runIdentityAction(async () => {
      const redirectIdentity =
        await this.googleIdentityService.completeRedirectSignIn();

      return (
        redirectIdentity ??
        (await this.googleIdentityService.getCurrentIdentity())
      );
    });
  }

  public onGoogleRegister(newAccount: GoogleRegister, logo: File | null): void {
    void this.submitGoogleRegistration(newAccount, logo);
  }

  public confirmEmail(
    token: string,
  ): Observable<
    ApiResponse<{ emailVerified: boolean; accountActivated: boolean }>
  > {
    return this.httpClient.post<
      ApiResponse<{ emailVerified: boolean; accountActivated: boolean }>
    >(
      `${this.AUTH_URL}/email-confirmation/confirm`,
      { token },
      {
        headers: skipLoaderHeaders(),
      },
    );
  }

  public resendEmailConfirmation(
    email: string,
  ): Observable<ApiResponse<string>> {
    return this.httpClient.post<ApiResponse<string>>(
      `${this.AUTH_URL}/email-confirmation/resend`,
      { email },
      { headers: skipLoaderHeaders() },
    );
  }

  public forgotPassword(email: string): Observable<ApiResponse<string>> {
    return this.httpClient.post<ApiResponse<string>>(
      `${this.AUTH_URL}/password/forgot`,
      { email },
      { headers: skipLoaderHeaders() },
    );
  }

  public resetPassword(
    token: string,
    password: string,
    passwordConfirmation: string,
  ): Observable<ApiResponse<string>> {
    return this.httpClient.post<ApiResponse<string>>(
      `${this.AUTH_URL}/password/reset`,
      { token, password, passwordConfirmation },
      { headers: skipLoaderHeaders() },
    );
  }

  public async cancelGoogleRegistration(): Promise<void> {
    sessionStorage.removeItem(this.GOOGLE_REGISTRATION_PENDING_KEY);
    sessionStorage.removeItem(PLATFORM_NOTICES_SESSION_KEY);

    try {
      await this.googleIdentityService.signOut();
    } catch (error: unknown) {
      this.notificationService.showToastError(
        error,
        'No se pudo cerrar la sesión de Google. Intenta nuevamente.',
      );
    }
  }

  public onLogout(): void {
    void this.logout();
  }

  private async runGoogleLogin(
    identityProvider: () => Promise<GoogleIdentity | null>,
  ): Promise<void> {
    if (!this.isGoogleAuthEnabled || this.isLoading) return;

    this.loadingSubject.next(true);

    try {
      const identity = await identityProvider();
      if (!identity) return;

      const response = await firstValueFrom(
        this.httpClient.post<ApiResponse<LoginData>>(
          `${this.AUTH_URL}/google/login`,
          { idToken: identity.idToken },
          { headers: skipLoaderHeaders() },
        ),
      );

      sessionStorage.removeItem(this.GOOGLE_REGISTRATION_PENDING_KEY);
      this.persistSuccessfulLogin(response);
    } catch (error: unknown) {
      if (this.apiErrors.from(error).code === 'GOOGLE_REGISTRATION_REQUIRED') {
        sessionStorage.setItem(this.GOOGLE_REGISTRATION_PENDING_KEY, 'true');
        await this.router.navigate(['/register'], {
          queryParams: { provider: 'google' },
        });
        return;
      }

      this.notificationService.showToastError(
        error,
        'No se pudo iniciar sesión con Google. Intenta nuevamente.',
      );
    } finally {
      this.loadingSubject.next(false);
    }
  }

  private async runIdentityAction(
    action: () => Promise<GoogleIdentity | null>,
  ): Promise<GoogleIdentity | null> {
    if (!this.isGoogleAuthEnabled || this.isLoading) return null;

    this.loadingSubject.next(true);

    try {
      return await action();
    } catch (error: unknown) {
      this.notificationService.showToastError(
        error,
        'No se pudo conectar la cuenta de Google. Intenta nuevamente.',
      );
      return null;
    } finally {
      this.loadingSubject.next(false);
    }
  }

  private async submitGoogleRegistration(
    newAccount: GoogleRegister,
    logo: File | null,
  ): Promise<void> {
    if (this.isLoading) return;

    this.loadingSubject.next(true);

    try {
      const identity = await this.googleIdentityService.getCurrentIdentity();
      if (!identity) {
        throw new Error(
          'La sesión de Google venció. Volvé a vincular tu cuenta.',
        );
      }

      const company = {
        ...newAccount.company,
        logo: undefined,
      };
      const formData = new FormData();

      formData.append('idToken', identity.idToken);
      formData.append('company', JSON.stringify(company));
      formData.append('user', JSON.stringify(newAccount.user));
      if (logo) formData.append('logo', logo, logo.name);

      const response = await firstValueFrom(
        this.httpClient.post<ApiResponse<RegistrationResponse>>(
          `${this.AUTH_URL}/google/register`,
          formData,
          { headers: skipLoaderHeaders() },
        ),
      );

      sessionStorage.removeItem(this.GOOGLE_REGISTRATION_PENDING_KEY);
      try {
        await this.googleIdentityService.signOut();
      } catch {
        // Registration succeeded; Firebase cleanup must not hide that result.
      }
      this.finishRegistration(response);
    } catch (error: unknown) {
      this.notificationService.showToastError(
        error,
        'No se pudo completar el registro con Google. Intenta nuevamente.',
      );
    } finally {
      this.loadingSubject.next(false);
    }
  }

  private persistSuccessfulLogin(data: ApiResponse<LoginData>): void {
    try {
      const { user, token } = data.payload;
      const persisted = this.accountService.persistSession(
        user,
        user.company,
        token,
      );

      if (!persisted) return;

      this.arcaElectronicBillingService.loadElectronicBillingIntegrationStatus();
      this.notificationService.showTotast(`Bienvenido ${user.name}!`);
      void this.router.navigate(['home']);
    } catch {
      this.notificationService.showToastError(
        'No se pudo iniciar sesión: error interno al guardar datos localmente.',
      );
    }
  }

  private finishRegistration(data: ApiResponse<RegistrationResponse>): void {
    sessionStorage.removeItem('register_draft');
    void this.router.navigate(['/registration-pending'], {
      state: {
        email: data.payload.email,
        emailDeliveryStatus: data.payload.emailDeliveryStatus,
      },
    });
  }

  private async finishLogout(logoutError: unknown | null): Promise<void> {
    this.arcaElectronicBillingService.clearCatalogCache();
    this.accountService.clearSession();
    sessionStorage.removeItem(this.GOOGLE_REGISTRATION_PENDING_KEY);

    try {
      await this.googleIdentityService.signOut();
    } catch {
      // The local GarageDigital session is cleared even if Firebase sign-out fails.
    }

    if (logoutError === null) {
      this.notificationService.showTotast('Sesión finalizada');
    } else {
      this.notificationService.showToastError(
        logoutError,
        'Error al cerrar sesión, pero se limpió la sesión local.',
      );
    }

    await this.router.navigate(['login']);
  }

  private async logout(): Promise<void> {
    if (this.isLoading) return;

    this.loadingSubject.next(true);
    let logoutError: unknown | null = null;

    try {
      await firstValueFrom(
        this.httpClient.post(
          `${this.AUTH_URL}/logout`,
          {},
          {
            headers: skipLoaderHeaders(),
          },
        ),
      );
    } catch (error: unknown) {
      logoutError = error;
    }

    try {
      await this.finishLogout(logoutError);
    } finally {
      this.loadingSubject.next(false);
    }
  }
}
